import * as React from 'react';
import { useState, useEffect } from 'react';
import _ from 'underscore';

import { hashToCollection, SortableList, TextEditor } from '../../index';

export interface IncorrectSequence {
  key: string;
  name?: string;
  text: string;
  feedback: string;
  order: number;
  conceptResults?: { [key: string]: { name: string; correct: boolean } };
}

export interface IncorrectSequenceContainerProps {
  baseUrl: string;
  incorrectSequences: { [key: string]: IncorrectSequence };
  onDeleteSequence: (questionID: string, sequenceID: string) => void;
  onMount?: (questionID: string) => void;
  onSubmitEditedSequence: (questionID: string, data: IncorrectSequence, key: string) => void;
  onUpdateSequences: (questionID: string, sequences: { [key: string]: IncorrectSequence }, callback?: () => void) => void;
  questionID: string;
}

const IncorrectSequencesContainer: React.FC<IncorrectSequenceContainerProps> = ({
  baseUrl,
  incorrectSequences: initialIncorrectSequences,
  onDeleteSequence,
  onMount,
  onSubmitEditedSequence,
  onUpdateSequences,
  questionID
}) => {
  const sequencesCollection = hashToCollection(initialIncorrectSequences);
  const initialOrderedIds = sequencesCollection.sort((a, b) => a.order - b.order).map((b) => b.key);

  const [orderedIds, setOrderedIds] = useState<string[]>(initialOrderedIds);
  const [incorrectSequences, setIncorrectSequences] = useState(initialIncorrectSequences);

  // Call onMount callback when component mounts (used by Grammar for getUsedSequences)
  useEffect(() => {
    if (onMount) {
      onMount(questionID);
    }
  }, []);

  // Update state when props change
  useEffect(() => {
    const incomingOrderedIds = hashToCollection(initialIncorrectSequences)
      .sort((a, b) => a.order - b.order)
      .map((seq) => seq.key);

    if (!_.isEqual(incorrectSequences, initialIncorrectSequences)) {
      setIncorrectSequences(initialIncorrectSequences);
      setOrderedIds(incomingOrderedIds);
    }
  }, [initialIncorrectSequences]);

  const alertSaved = () => {
    alert('Saved!');
  };

  const alertDeleted = () => {
    alert('Deleted!');
  };

  const deleteSequence = (sequenceID: string) => {
    if (confirm('⚠️ Are you sure you want to delete this? 😱')) {
      onDeleteSequence(questionID, sequenceID);

      const updatedSequences = { ...incorrectSequences };
      delete updatedSequences[sequenceID];

      const newOrderedIds = orderedIds.filter((id) => id !== sequenceID);
      const incorrectSequenceArray = hashToCollection(updatedSequences);

      const newIncorrectSequences: { [key: string]: IncorrectSequence } = {};
      newOrderedIds.forEach((id, index) => {
        const sequence = incorrectSequenceArray.find((is) => is.key === id);
        if (sequence) {
          sequence.order = index;
          newIncorrectSequences[sequence.key] = sequence;
        }
      });

      if (incorrectSequenceArray.length > 0) {
        onUpdateSequences(questionID, newIncorrectSequences, alertDeleted);
      } else {
        alertDeleted();
      }

      setOrderedIds(newOrderedIds);
      setIncorrectSequences(newIncorrectSequences);
    }
  };

  const deleteConceptResult = (conceptResultKey: string, sequenceKey: string) => {
    if (confirm('⚠️ Are you sure you want to delete this? 😱')) {
      const data = { ...incorrectSequences[sequenceKey] };
      if (data.conceptResults) {
        delete data.conceptResults[conceptResultKey];
      }
      onSubmitEditedSequence(questionID, data, sequenceKey);
    }
  };

  const handleDeleteConceptResult = (key: string, sequenceKey: string) => {
    deleteConceptResult(key, sequenceKey);
  };

  const handleDeleteSequence = (key: string) => {
    deleteSequence(key);
  };

  const removeEmptySequences = (sequences: { [key: string]: IncorrectSequence }) => {
    return _.mapObject(sequences, (val) =>
      Object.assign({}, val, {
        text: val.text
          .split(/\|{3}(?!\|)/)
          .filter((val) => val !== '')
          .join('|||'),
      })
    );
  };

  const addNewSequence = (e: React.MouseEvent, key: string) => {
    const className = `regex-${key}`;
    const value = `${Array.from(document.getElementsByClassName(className))
      .map((i: any) => i.value)
      .filter((val) => val !== '')
      .join('|||')}|||`;

    const updatedSequences = { ...incorrectSequences };
    updatedSequences[key].text = value;
    setIncorrectSequences(updatedSequences);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const updatedSequences = { ...incorrectSequences };
    updatedSequences[key].name = e.target.value;
    setIncorrectSequences(updatedSequences);
  };

  const handleSequenceChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const className = `regex-${key}`;
    const value = `${Array.from(document.getElementsByClassName(className))
      .map((i: any) => i.value)
      .filter((val) => val !== '')
      .join('|||')}`;

    if (value === '') {
      if (!confirm('Deleting this regex will delete the whole incorrect sequence. Are you sure you want that?')) {
        return;
      }
    }

    const updatedSequences = { ...incorrectSequences };
    updatedSequences[key].text = value;
    setIncorrectSequences(updatedSequences);
  };

  const handleFeedbackChange = (feedback: string, key: string) => {
    const updatedSequences = { ...incorrectSequences };
    updatedSequences[key].feedback = feedback;
    setIncorrectSequences(updatedSequences);
  };

  const inputElement = (className: string, text: string, key: string) => {
    return (
      <input
        aria-label="Regex pattern"
        className={className}
        onChange={(e) => handleSequenceChange(e, key)}
        style={{ marginBottom: 5, minWidth: `${(text.length + 1) * 8}px` }}
        type="text"
        value={text || ''}
      />
    );
  };

  const sequencesSortedByOrder = () => {
    const sequencesCollection = hashToCollection(incorrectSequences);
    return orderedIds.map((id) => sequencesCollection.find((s) => s.key === id)).filter(Boolean) as IncorrectSequence[];
  };

  const sortCallback = (sortInfo: any[]) => {
    const newOrderedIds = sortInfo.map((item) => item.key);
    setOrderedIds(newOrderedIds);
  };

  const handleSaveAllSequences = () => {
    const newIncorrectSequences: { [key: string]: IncorrectSequence } = {};
    const incorrectSequenceArray = hashToCollection(incorrectSequences);

    orderedIds.forEach((id, index) => {
      const sequence = incorrectSequenceArray.find((is) => is.key === id);
      if (sequence) {
        sequence.order = index;
        newIncorrectSequences[sequence.key] = sequence;
      }
    });

    onUpdateSequences(questionID, newIncorrectSequences, alertSaved);
  };

  const renderConceptResults = (concepts: { [key: string]: { name: string; correct: boolean } } | undefined, sequenceKey: string) => {
    if (concepts) {
      const components = _.mapObject(concepts, (val, key) => (
        <p className="control sub-title is-6" key={`${val.name}`}>
          {val.name}
          {val.correct ? (
            <span className="tag is-small is-success" style={{ marginLeft: 5 }}>
              Correct
            </span>
          ) : (
            <span className="tag is-small is-danger" style={{ marginLeft: 5 }}>
              Incorrect
            </span>
          )}
          <button
            className="tag is-small is-warning"
            onClick={() => handleDeleteConceptResult(key, sequenceKey)}
            style={{ cursor: 'pointer', marginLeft: 5 }}
            type="button"
          >
            Delete
          </button>
        </p>
      ));
      return _.values(components);
    }
  };

  const renderTextInputFields = (sequenceString: string, key: string) => {
    const className = `input regex-inline-edit regex-${key}`;
    if (sequenceString === '') return inputElement(className, '', key);
    return sequenceString.split(/\|{3}(?!\|)/).map((text, index) => (
      <React.Fragment key={`${key}-${index}`}>
        {inputElement(className, text, key)}
      </React.Fragment>
    ));
  };

  const renderSaveButton = () => {
    return (
      <button
        className="button is-outlined is-primary"
        onClick={handleSaveAllSequences}
        style={{ float: 'right' }}
        type="button"
      >
        Save Sequences
      </button>
    );
  };

  const renderSequenceList = () => {
    const components = sequencesSortedByOrder().map((seq) => {
      const onClickDelete = () => {
        handleDeleteSequence(seq.key);
      };

      return (
        <div className="card is-fullwidth has-bottom-margin" id={seq.key} key={seq.key}>
          <header className="card-header">
            <input
              aria-label="Sequence name"
              className="regex-name"
              onChange={(e) => handleNameChange(e, seq.key)}
              placeholder="Name"
              type="text"
              value={seq.name || ''}
            />
          </header>
          <header className="card-header">
            <p className="card-header-title" style={{ display: 'inline-block' }}>
              {renderTextInputFields(seq.text, seq.key)}
              <button className="add-regex-button" onClick={(e) => addNewSequence(e, seq.key)} type="button">
                +
              </button>
            </p>
            <p className="card-header-icon">{seq.order}</p>
          </header>
          <div className="card-content">
            <label className="label" htmlFor="feedback" style={{ marginTop: 10 }}>
              Feedback
            </label>
            <TextEditor handleTextChange={(e) => handleFeedbackChange(e, seq.key)} key="feedback" text={seq.feedback} />
            <br />
            {renderConceptResults(seq.conceptResults, seq.key)}
          </div>
          <footer className="card-footer">
            <a className="card-footer-item" href={`${baseUrl}/${seq.key}/edit`}>
              Edit
            </a>
            <button className="card-footer-item" onClick={onClickDelete} type="button">
              Delete
            </button>
          </footer>
        </div>
      );
    });

    return <SortableList data={_.values(components)} key={_.values(components).length} sortCallback={sortCallback} />;
  };

  return (
    <div>
      <div className="has-top-margin">
        <h1 className="title is-3" style={{ display: 'inline-block' }}>
          Incorrect Sequences
        </h1>
        <a
          className="button is-outlined is-primary"
          href={`${baseUrl}/new`}
          rel="noopener noreferrer"
          style={{ float: 'right' }}
        >
          Add Incorrect Sequence
        </a>
        {renderSaveButton()}
      </div>
      {renderSequenceList()}
    </div>
  );
};

export { IncorrectSequencesContainer }
